/**
 * The review: exactly what adding will create and change (the server's dry run), as a map of the space after it and
 * entity by entity, with the links it makes, the optional ones to keep or leave out, entities worth adding for the
 * links still without a target, and the field keys (in the person's language, or English for integrations).
 */

import type { ComponentChildren } from "preact";
import type { ApiError } from "../../api/client.ts";
import type {
  FieldType,
  ObjEntity,
  PlannedEntity,
  SpaceOut,
  SuggestedLink,
  TemplateField,
  TemplateOut,
  TemplatePlan,
  TextFormat,
} from "../../api/types.gen.ts";
import type { Key } from "../../i18n/index.ts";
import { t } from "../../i18n/index.ts";
import { facetsOf, hintsByTarget, planLinks } from "../../lib/catalog.ts";
import { lazy } from "../../lib/lazy.ts";
import { useResource } from "../../lib/resource.ts";
import { useId } from "../../lib/uid.ts";
import { Button } from "../../ui/Button.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { Checkbox, Switch } from "../../ui/form.tsx";
import { Async } from "../../ui/misc.tsx";

/** Where a ref field links: an entity added with it, one the space has, or any item. */
function targetName(f: TemplateField, plan: TemplatePlan, existing: readonly ObjEntity[]): string {
  const link = f.links?.[0];
  if (link) {
    return plan.entities.find((e) => e.key === link)?.name ?? link;
  }
  if (f.target) {
    return existing.find((e) => e.id === f.target)?.name ?? f.target;
  }
  return t("catalog.anyItem");
}

/** A type's name, or its format's (a text field's). */
const typeName = (type: FieldType, format?: TextFormat | null): string =>
  format ? t(`format.${format}` as Key) : t(`type.${type}` as Key);

/** A field's key, as the API and filters see it. */
const KeyText = ({ k }: { k: string }) => <code class="font-mono text-xs text-zinc-500">{k}</code>;

function Planned({ e, plan, existing }: { e: PlannedEntity; plan: TemplatePlan; existing: readonly ObjEntity[] }) {
  const merge = e.action === "merge";
  return (
    <li class="rounded-lg border border-zinc-200 bg-white p-4">
      <div class="mb-3 flex flex-wrap items-center gap-2">
        <EntityGlyph icon={e.icon ?? existing.find((x) => x.id === e.entity)?.icon} size={18} pad={4} empty="dot" />
        <h3 class="font-medium text-zinc-900">{e.name}</h3>
        <Chip tone={merge ? "amber" : "green"}>
          {merge ? t("catalog.merged", { count: e.fields.length }) : t("catalog.created")}
        </Chip>
      </div>
      {e.fields.length === 0 ? (
        <p class="text-sm text-zinc-500">{t("catalog.nothingToAdd")}</p>
      ) : (
        <ul class="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {e.fields.map((f) => (
            <li key={f.key} class="flex min-w-0 items-baseline justify-between gap-3 border-b border-zinc-100 py-1">
              <span class="flex min-w-0 items-baseline gap-2">
                <span class="truncate text-zinc-900">{f.label ?? f.key}</span>
                <KeyText k={f.key} />
              </span>
              <span class="shrink-0 text-xs text-zinc-500">
                {f.type === "ref" ? `→ ${targetName(f, plan, existing)}` : typeName(f.type, f.format)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {e.kept && e.kept.length > 0 ? (
        <p class="mt-2 text-xs text-zinc-500">{t("catalog.kept", { fields: e.kept.join(", ") })}</p>
      ) : null}
      {(e.differ ?? []).map((d) => (
        <p key={d.key} class="mt-1 text-xs text-amber-800">
          {t("catalog.keptType", {
            field: d.key,
            type: typeName(d.type, d.format),
            catalog: typeName(d.catalog_type, d.catalog_format),
          })}
        </p>
      ))}
    </li>
  );
}

/** A section of the review: a heading, maybe a line under it, and what it holds. */
function Section(p: { title: string; hint?: string; children: ComponentChildren }) {
  const id = useId();
  return (
    <section aria-labelledby={id} class="rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id={id} class="font-medium text-zinc-900">
        {p.title}
      </h2>
      {p.hint ? <p class="mt-0.5 text-sm text-zinc-600">{p.hint}</p> : null}
      <div class="mt-3">{p.children}</div>
    </section>
  );
}

/** What the review can change: links kept or left out, facets, entities added, the keys. */
export interface ReviewActions {
  onLink: (entity: string, field: string, on: boolean) => void;
  onFacet: (entity: string, facet: string) => void;
  onAdd: (key: string) => void;
  onEnglish: (on: boolean) => void;
}

function Suggestion(p: {
  s: SuggestedLink;
  plan: TemplatePlan;
  entities: readonly TemplateOut[];
  actions: ReviewActions;
}) {
  const { s } = p;
  const from = p.plan.entities.find((e) => e.key === s.entity)?.name ?? s.entity;
  const facet = s.facet
    ? facetsOf(p.entities.find((e) => e.key === s.entity) as TemplateOut).find((f) => f.key === s.facet)?.name
    : undefined;
  return (
    <li class="flex flex-col gap-0.5">
      <Checkbox
        checked={s.chosen}
        onCheck={(on) => (s.facet ? p.actions.onFacet(s.entity, s.facet) : p.actions.onLink(s.entity, s.field, on))}
        label={<span>{t("catalog.link", { from, to: s.target_name, field: s.label })}</span>}
      />
      {facet ? <span class="pl-6 text-xs text-zinc-500">{t("catalog.withFacet", { facet })}</span> : null}
    </li>
  );
}

/** The map's chunk (a page opened before a deploy reloads for it: lib/lazy.ts). */
const mapChunk = lazy(() => import("./map.tsx"));

/** The map of the space after adding, loaded the first time it's shown. */
function MapPreview(p: { space: SpaceOut; plan: TemplatePlan; existing: readonly ObjEntity[] }) {
  const code = useResource("catalog-map", mapChunk);
  return <Async r={code}>{(m) => <m.PlanMap {...p} />}</Async>;
}

export function Review(p: {
  space: SpaceOut;
  plan: TemplatePlan;
  existing: readonly ObjEntity[];
  /** The catalog's entities (their facets' names). */
  entities: readonly TemplateOut[];
  english: boolean;
  actions: ReviewActions;
  /** The preview's error, if adding as it is would fail. */
  error?: ApiError | undefined;
  /** Back and Add: at the top right, by the intro, and again at the end (on phones, in a bar at the foot of the screen, like Review's). */
  buttons: ComponentChildren;
}) {
  const { plan, existing } = p;
  const links = planLinks(plan, existing);
  const hints = hintsByTarget(plan);
  return (
    <div class="flex flex-col gap-4 pb-20 lg:pb-0">
      <div class="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div class="min-w-0 flex-1 basis-80">
          <p class="text-sm text-zinc-600">{t("catalog.reviewIntro", { count: plan.entity_count })}</p>
          {p.error ? (
            <p role="alert" class="mt-2 text-sm text-red-700">
              {p.error.message}
            </p>
          ) : null}
        </div>
        <div class="hidden shrink-0 flex-wrap items-center gap-2 lg:flex">{p.buttons}</div>
      </div>
      <Section title={t("catalog.mapTitle")} hint={t("catalog.mapHint")}>
        <MapPreview space={p.space} plan={plan} existing={existing} />
      </Section>
      <Section title={t("catalog.links")}>
        {links.length === 0 ? (
          plan.suggestions.length === 0 ? (
            <p class="text-sm text-zinc-500">{t("catalog.linksNone")}</p>
          ) : null
        ) : (
          <ul class="flex flex-col gap-1 text-sm text-zinc-800">
            {links.map((l) => (
              <li key={`${l.from}.${l.key}`}>
                {t("catalog.link", { from: l.from, to: l.to, field: l.label })} <KeyText k={l.key} />
              </li>
            ))}
          </ul>
        )}
        {plan.suggestions.length > 0 ? (
          <div class={links.length > 0 ? "mt-4 border-t border-zinc-100 pt-3" : ""}>
            <h3 class="text-sm font-medium text-zinc-900">{t("catalog.suggested")}</h3>
            <p class="mb-2 text-xs text-zinc-500">{t("catalog.suggestedHint")}</p>
            <ul class="flex flex-col gap-2">
              {plan.suggestions.map((s) => (
                <Suggestion
                  key={`${s.entity}.${s.field}`}
                  s={s}
                  plan={plan}
                  entities={p.entities}
                  actions={p.actions}
                />
              ))}
            </ul>
          </div>
        ) : null}
        {hints.length > 0 ? (
          <ul class="mt-4 flex flex-col gap-2 border-t border-zinc-100 pt-3">
            {hints.map((h) => (
              <li key={h.add} class="flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-700">
                <span class="min-w-0 flex-1">{t("catalog.hint", { name: h.name, entities: h.from.join(", ") })}</span>
                <Button size="sm" onClick={() => p.actions.onAdd(h.add)}>
                  {t("catalog.hintAdd", { name: h.name })}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </Section>
      <div class="flex flex-col gap-1">
        <Switch checked={p.english} onCheck={p.actions.onEnglish} label={t("catalog.englishKeys")} />
        <p class="text-xs text-zinc-500">{t("catalog.englishKeysHint")}</p>
      </div>
      <ul class="flex flex-col gap-3">
        {plan.entities.map((e) => (
          <Planned key={e.key} e={e} plan={plan} existing={existing} />
        ))}
      </ul>
      {plan.unlinked.length > 0 ? (
        <div class="rounded-md bg-zinc-50 px-4 py-3 text-sm text-zinc-700">
          <p class="font-medium">{t("catalog.unlinked")}</p>
          <ul class="mt-1 list-disc pl-5">
            {plan.unlinked.map((u) => (
              <li key={`${u.entity}.${u.field}`}>
                {t("catalog.unlinkedOne", {
                  entity: plan.entities.find((e) => e.key === u.entity)?.name ?? u.entity,
                  field:
                    plan.entities.find((e) => e.key === u.entity)?.fields.find((f) => f.key === u.field)?.label ??
                    u.field,
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {/* The page is long: the same two again at its end, where reading it stops (on phones, the bar does that). */}
      <div class="hidden justify-end gap-2 border-t border-zinc-200 pt-4 lg:flex">{p.buttons}</div>
      <div class="fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-2 border-t border-zinc-200 bg-white px-4 py-3 lg:hidden">
        {p.buttons}
      </div>
    </div>
  );
}
